import React, { useEffect, useState } from 'react';
import { ethers } from 'ethers';
import Input from '../../components/common/Input/Input';
import Button from '../../components/common/Button/Button';
import { useNotification } from '../../context/NotificationContext';
import { Section, StatGrid, Loading } from './AdminUI';
import { pctToBps, bpsToPct } from './useAdmin';

// id = walletId / feeId used by Storage.setWallet / setFeePercent
// Router pool (feeId 5) is intentionally NOT part of this screen
const WALLETS = [
  { id: 1, key: 'teamWallet1', label: 'Team Wallet 1', pct: 'teamWallet1Percent' },
  { id: 2, key: 'teamWallet2', label: 'Team Wallet 2', pct: 'teamWallet2Percent' },
  { id: 3, key: 'mainWallet', label: 'Main Wallet', pct: 'mainWalletPercent' },
  { id: 4, key: 'mWallet', label: 'M Wallet', pct: 'mWalletPercent', ownerOnly: true }
];

const AdminWallets = ({ admin }) => {
  const { read, runTx, pending, isOwner } = admin;
  const { error: showError } = useNotification();
  const [form, setForm] = useState(null);
  const [onChain, setOnChain] = useState(null);

  const load = async () => {
    try {
      const values = await Promise.all(
        WALLETS.flatMap((w) => [read.storage[w.key](), read.storage[w.pct]()])
      );
      const next = {};
      WALLETS.forEach((w, i) => {
        next[w.key] = { address: values[i * 2], pct: bpsToPct(values[i * 2 + 1]) };
      });
      setForm(JSON.parse(JSON.stringify(next)));
      setOnChain(next);
    } catch (err) {
      console.error('Admin wallets load error:', err);
    }
  };

  useEffect(() => { load(); }, [read]);

  if (!form) return <Loading />;

  const update = (key, field) => (e) =>
    setForm({ ...form, [key]: { ...form[key], [field]: e.target.value.trim() } });

  const totalOut = WALLETS.reduce((sum, w) => sum + (parseFloat(onChain[w.key].pct) || 0), 0);

  const saveAddress = async (w) => {
    const addr = form[w.key].address;
    if (!ethers.utils.isAddress(addr) || addr === ethers.constants.AddressZero) {
      return showError('Enter a valid wallet address');
    }
    const ok = await runTx(`${w.key}-addr`, `${w.label} update`, (c) => c.storage.setWallet(w.id, addr));
    if (ok) load();
  };

  const savePercent = async (w) => {
    const pct = parseFloat(form[w.key].pct);
    if (isNaN(pct) || pct < 0 || pct > 50) return showError('Percentage must be 0 - 50%');

    const newTotal = totalOut - parseFloat(onChain[w.key].pct) + pct;
    if (newTotal > 100) {
      return showError(isOwner ? `Total wallet share would be ${newTotal}% (over 100%)` : 'Total wallet share would exceed 100%');
    }

    const ok = await runTx(`${w.key}-pct`, `${w.label} % update`, (c) => c.storage.setFeePercent(w.id, pctToBps(pct)));
    if (ok) load();
  };

  return (
    <>
      {/* Totals include the M wallet share, so only the owner sees them */}
      {isOwner && (
        <StatGrid items={[
          { label: 'Sent to Wallets', value: `${totalOut}%` },
          { label: 'Kept in Contract', value: `${Math.max(0, 100 - totalOut)}%` }
        ]} />
      )}

      {WALLETS.filter((w) => isOwner || !w.ownerOnly).map((w) => {
        const addrChanged = form[w.key].address.toLowerCase() !== onChain[w.key].address.toLowerCase();
        const pctChanged = form[w.key].pct !== onChain[w.key].pct;
        return (
          <Section key={w.key} title={w.label} subtitle={`${onChain[w.key].pct}% of every deposit`}>
            <Input label="Address" className="mono" value={form[w.key].address} onChange={update(w.key, 'address')} />
            <Button
              fullWidth
              variant="secondary"
              onClick={() => saveAddress(w)}
              loading={pending === `${w.key}-addr`}
              disabled={!addrChanged}
            >
              Save Address
            </Button>
            <Input label="Share" type="number" suffix="%" value={form[w.key].pct} onChange={update(w.key, 'pct')} />
            <Button
              fullWidth
              variant="secondary"
              onClick={() => savePercent(w)}
              loading={pending === `${w.key}-pct`}
              disabled={!pctChanged}
            >
              Save Share
            </Button>
          </Section>
        );
      })}
    </>
  );
};

export default AdminWallets;
